/**
 * Message reaction helpers – shared by web (Chat.tsx) and native (ChatScreen.native.tsx).
 * Mission 30: Cedar "reacts" to user messages with context-aware emojis.
 */

export interface MessageReaction {
  emoji: string;
  source: 'cedar' | 'user';
  timestamp: string;
}

export interface MessageContext {
  primaryEmotion: 'vulnerability' | 'achievement' | 'gratitude' | 'humor' | 'goals' | 'setbacks' | 'neutral';
  significanceLevel: number;
  isPersonalShare: boolean;
  hasEmotionalWeight: boolean;
  messageLength: number;
}

/**
 * Analyze user message for emotional context and significance (1–10).
 */
export function analyzeMessageContext(userMessage: string): MessageContext {
  const lowerMessage = userMessage.toLowerCase();
  const messageLength = userMessage.length;

  const vulnerabilityWords = ['struggling', 'difficult', 'hard', 'scared', 'worried', 'anxious', 'frustrated', 'overwhelmed', 'stuck'];
  const hasVulnerability = vulnerabilityWords.some((w) => lowerMessage.includes(w));

  const achievementWords = ['accomplished', 'achieved', 'completed', 'finished', 'succeeded', 'proud', 'excited', 'breakthrough', 'progress', 'won'];
  const hasAchievement = achievementWords.some((w) => lowerMessage.includes(w));

  const gratitudeWords = ['thank', 'appreciate', 'grateful', 'helpful', 'amazing', 'wonderful', 'perfect'];
  const hasGratitude = gratitudeWords.some((w) => lowerMessage.includes(w));

  const goalWords = ['goal', 'plan', 'want to', 'going to', 'will', 'commit', 'promise', 'determined'];
  const hasGoals = goalWords.some((w) => lowerMessage.includes(w));

  const setbackWords = ['failed', 'disappointed', 'regressed', 'worse', 'setback', 'gave up', 'quit'];
  const hasSetbacks = setbackWords.some((w) => lowerMessage.includes(w));

  const humorWords = ['haha', 'lol', 'funny', 'joke', 'hilarious', '😂', '😄', '😆'];
  const hasHumor = humorWords.some((w) => lowerMessage.includes(w));

  let primaryEmotion: MessageContext['primaryEmotion'] = 'neutral';
  let significanceLevel = 3;

  if (hasVulnerability) {
    primaryEmotion = 'vulnerability';
    significanceLevel = 8;
  } else if (hasAchievement) {
    primaryEmotion = 'achievement';
    significanceLevel = 9;
  } else if (hasGratitude) {
    primaryEmotion = 'gratitude';
    significanceLevel = 7;
  } else if (hasSetbacks) {
    primaryEmotion = 'setbacks';
    significanceLevel = 8;
  } else if (hasGoals) {
    primaryEmotion = 'goals';
    significanceLevel = 6;
  } else if (hasHumor) {
    primaryEmotion = 'humor';
    significanceLevel = 5;
  }

  if (messageLength > 100) significanceLevel = Math.min(10, significanceLevel + 1);
  if (messageLength > 200) significanceLevel = Math.min(10, significanceLevel + 1);

  return {
    primaryEmotion,
    significanceLevel,
    isPersonalShare: messageLength > 50 && (hasVulnerability || hasAchievement || hasSetbacks),
    hasEmotionalWeight: hasVulnerability || hasAchievement || hasSetbacks || hasGratitude,
    messageLength,
  };
}

/**
 * Whether Cedar should add a reaction given context and user warmth/humor (1–10).
 */
export function shouldCedarReact(
  context: MessageContext,
  userWarmth: number,
  userHumor: number
): boolean {
  if (userWarmth <= 2) return false;
  if (context.significanceLevel >= 8) return true;
  if (userWarmth >= 8) return context.significanceLevel >= 4;
  if (userWarmth >= 5) return context.significanceLevel >= 6;
  return context.significanceLevel >= 7;
}

/**
 * Pick one emoji for the reaction from context and user warmth/humor (1–10).
 */
export function getContextualEmoji(
  context: MessageContext,
  userWarmth: number,
  userHumor: number
): string | null {
  const emojiSets: Record<MessageContext['primaryEmotion'], string[] | null> = {
    vulnerability: userWarmth >= 7 ? ['❤️', '🫂', '💙'] : ['💙'],
    achievement: userHumor >= 6 ? ['🎉', '💪', '⭐', '🚀'] : ['💪', '⭐'],
    gratitude: ['😊', '🙏', '💚'],
    goals: ['🎯', '💪', '⚡'],
    setbacks: ['💙', '🤗'],
    humor: userHumor >= 6 ? ['😄', '😊', '🤣'] : ['😊'],
    neutral: userWarmth >= 7 ? ['😊', '👍'] : [],
  };

  const availableEmojis = emojiSets[context.primaryEmotion];
  if (!availableEmojis || availableEmojis.length === 0) return null;
  return availableEmojis[Math.floor(Math.random() * availableEmojis.length)];
}
